<template>
  <div class="w-full" :style="{ height: clientHeight + 'px' }">
    <NavBar v-if="!usesAuthLanding" @show-login="showLogin = true" />

    <!-- Login Modal opened from public pages such as home -->
    <div
      v-if="showLogin && !usesAuthLanding && !usesAuthErrorPage"
      class="fixed inset-0 z-50"
    >
      <LoginPage @close="showLogin = false" />
    </div>

    <div v-if="usesAuthErrorPage" class="min-h-screen bg-slate-950">
      <AuthErrorPage
        :message="authErrorPageMessage"
        @retry-login="retryLogin"
        @go-home="goHome"
      />
    </div>

    <div
      v-else-if="isProtectedAuthLoading"
      data-testid="auth-check-loading"
      class="min-h-screen flex items-center justify-center bg-slate-900"
    >
      <div
        class="h-8 w-8 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"
      />
    </div>

    <LoginPage
      v-else-if="usesAuthLanding"
      @success="showLogin = false"
      @close="showLogin = false"
    />

    <!-- KeepAlive: 기존 v-show 방식의 동등 구현. 라우트 전환 시 컴포넌트 인스턴스를 유지하여
         Pinia에 없는 컴포넌트 로컬 상태(폼 입력값, 스크롤 위치 등)를 보존한다. -->
    <RouterView v-else v-slot="{ Component }">
      <KeepAlive>
        <component :is="Component" @show-login="showLogin = true" />
      </KeepAlive>
    </RouterView>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useStore } from "../store";
import { useAuthStore } from "../auth-store";
import { requiresAuthForNavView } from "../router/authPolicy";

import NavBar from "./NavBar.vue";
import LoginPage from "./LoginPage.vue";
import AuthErrorPage from "./AuthErrorPage.vue";
import { useI18n } from "../i18n";

// 서브픽셀 반올림으로 생기는 불필요한 스크롤바를 방지하는 높이 보정값(px).
const SUBPIXEL_SCROLLBAR_GUARD_PX = 0.01;
const AUTH_ERROR_PATH = "/auth/error";
const HOME_PATH = "/";

export default defineComponent({
  components: {
    NavBar,
    LoginPage,
    AuthErrorPage,
  },

  setup() {
    const { t } = useI18n();
    const store = useStore();
    const authStore = useAuthStore();
    const showLogin = ref(false);
    const usesAuthErrorPage = ref(window.location.pathname === AUTH_ERROR_PATH);

    const requiresAuth = computed(() => requiresAuthForNavView(store.navView));
    const authErrorPageMessage = computed(
      () => authStore.error ?? t("auth.genericError")
    );
    const isProtectedAuthLoading = computed(
      () =>
        requiresAuth.value && (!authStore.hasCheckedAuth || authStore.isLoading)
    );
    const usesAuthLanding = computed(
      () =>
        requiresAuth.value &&
        authStore.hasCheckedAuth &&
        !authStore.isLoading &&
        !authStore.isAuthenticated
    );

    const clientHeight = ref(0);

    const updateClientHeight = () => {
      // 뷰포트 높이를 그대로 사용한다. 모바일 주소창의 동적 높이를 반영하기 위해
      // 100vh(고정) 대신 documentElement.clientHeight 를 측정한다.
      clientHeight.value =
        document.documentElement.clientHeight - SUBPIXEL_SCROLLBAR_GUARD_PX;
    };

    updateClientHeight();
    window.addEventListener("resize", updateClientHeight);

    // 현재 뷰에 인증이 필요한지 확인
    const checkViewAccess = () => {
      if (!requiresAuth.value) {
        return;
      }

      if (
        authStore.hasCheckedAuth &&
        !authStore.isAuthenticated &&
        !authStore.isLoading
      ) {
        showLogin.value = true;
      }
    };

    const replaceCurrentUrl = (path: string) => {
      const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      window.history.replaceState(window.history.state, "", path);
      if (path !== currentUrl) {
        window.dispatchEvent(
          new PopStateEvent("popstate", { state: window.history.state })
        );
      }
    };

    const retryLogin = () => {
      usesAuthErrorPage.value = false;
      authStore.clearError();
      replaceCurrentUrl(HOME_PATH);
      showLogin.value = true;
    };

    const goHome = () => {
      usesAuthErrorPage.value = false;
      authStore.clearError();
      showLogin.value = false;
      replaceCurrentUrl(HOME_PATH);
    };

    // 마운트 시 인증 상태 확인
    onMounted(async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const isOAuthReturn = searchParams.get("auth") === "oauth";
      const oauthErrorCode = searchParams.get("auth_error");
      let hasOAuthCallbackError = oauthErrorCode !== null;
      if (oauthErrorCode !== null) {
        authStore.applyOAuthCallbackError(oauthErrorCode);
      }
      await authStore.checkAuth();
      if (isOAuthReturn && !authStore.isAuthenticated) {
        authStore.applyOAuthCallbackError("oauth_login_failed");
        hasOAuthCallbackError = true;
      }
      if (hasOAuthCallbackError && !authStore.isAuthenticated) {
        usesAuthErrorPage.value = true;
      }
      if (window.location.pathname === AUTH_ERROR_PATH && !authStore.error) {
        authStore.applyOAuthCallbackError("oauth_login_failed");
        usesAuthErrorPage.value = true;
      }
      if (isOAuthReturn || oauthErrorCode !== null) {
        searchParams.delete("auth");
        searchParams.delete("auth_error");
        searchParams.delete("provider");
        const nextSearch = searchParams.toString();
        const nextPath =
          hasOAuthCallbackError && !authStore.isAuthenticated
            ? AUTH_ERROR_PATH
            : window.location.pathname;
        const nextUrl = `${nextPath}${nextSearch ? `?${nextSearch}` : ""}${
          window.location.hash
        }`;
        replaceCurrentUrl(nextUrl);
      }
      checkViewAccess();
    });

    // 인증 적용을 위해 nav 뷰 변경 감시
    watch(
      () => store.navView,
      () => {
        checkViewAccess();
      }
    );

    // 인증 상태 변경 감시
    watch(
      () => authStore.isAuthenticated,
      (isAuthenticated) => {
        if (isAuthenticated) {
          usesAuthErrorPage.value = false;
          showLogin.value = false;
        } else {
          checkViewAccess();
        }
      }
    );

    return {
      store,
      clientHeight,
      showLogin,
      usesAuthErrorPage,
      authErrorPageMessage,
      isProtectedAuthLoading,
      usesAuthLanding,
      retryLogin,
      goHome,
    };
  },
});
</script>
